/**
 * JitsiStreamBackgroundEffect: TFLite 기반 실제 배경 분리 효과
 * 원본: wehago-conference/src/utils/JitsiVirtualBackground/JitsiStreamBackgroundEffect.js
 *
 * lib-jitsi-meet setEffect() 인터페이스 구현:
 *   isEnabled(jitsiLocalTrack): boolean
 *   startEffect(stream: MediaStream): MediaStream
 *   stopEffect(): void
 */

const VIRTUAL_BACKGROUND_TYPE = {
  IMAGE: 'image',
  BLUR: 'blur',
  NONE: 'none',
} as const;

type BackgroundType = (typeof VIRTUAL_BACKGROUND_TYPE)[keyof typeof VIRTUAL_BACKGROUND_TYPE];

export interface VirtualBackgroundConfig {
  backgroundType: BackgroundType;
  virtualSource?: string; // image URL (backgroundType === 'image')
  blurValue?: number;     // px for blur (backgroundType === 'blur')
}

interface TFLiteModel {
  HEAPF32: Float32Array;
  HEAPU8: Uint8Array;
  _getInputMemoryOffset(): number;
  _getOutputMemoryOffset(): number;
  _getModelBufferMemoryOffset(): number;
  _loadModel(size: number): void;
  _runInference(): void;
}

export class JitsiStreamBackgroundEffect {
  private _model: TFLiteModel;
  private _options: { width: number; height: number; virtualBackground: VirtualBackgroundConfig };
  private _stream: MediaStream | null = null;
  private _segmentationPixelCount: number;
  private _inputVideoElement: HTMLVideoElement;
  private _outputCanvasElement: HTMLCanvasElement;
  private _outputCanvasCtx: CanvasRenderingContext2D | null = null;
  private _segmentationMaskCtx: CanvasRenderingContext2D | null = null;
  private _segmentationMask: ImageData | null = null;
  private _segmentationMaskCanvas: HTMLCanvasElement;
  private _virtualImage: HTMLImageElement | null = null;
  private _timerId: ReturnType<typeof setTimeout> | null = null;
  private _isRunning = false;

  constructor(
    model: TFLiteModel,
    options: { width: number; height: number; virtualBackground: VirtualBackgroundConfig }
  ) {
    this._model = model;
    this._options = options;
    this._segmentationPixelCount = options.width * options.height;

    if (
      options.virtualBackground.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE &&
      options.virtualBackground.virtualSource
    ) {
      this._virtualImage = document.createElement('img');
      this._virtualImage.crossOrigin = 'anonymous';
      this._virtualImage.src = options.virtualBackground.virtualSource;
    }

    // Workaround for FF issue https://bugzilla.mozilla.org/show_bug.cgi?id=1388974
    this._outputCanvasElement = document.createElement('canvas');
    this._outputCanvasElement.getContext('2d');
    this._inputVideoElement = document.createElement('video');
    this._segmentationMaskCanvas = document.createElement('canvas');
  }

  /**
   * Checks if the local track supports this effect.
   * @param jitsiLocalTrack - The Jitsi local track to check.
   */
  isEnabled(jitsiLocalTrack: { isVideoTrack(): boolean; videoType?: string }): boolean {
    return jitsiLocalTrack.isVideoTrack() && jitsiLocalTrack.videoType === 'camera';
  }

  /**
   * Starts the effect. Returns the processed MediaStream.
   */
  startEffect(stream: MediaStream): MediaStream {
    this._stream = stream;
    this._isRunning = true;

    let firstVideoTrack: MediaStreamTrack | undefined;
    try {
      firstVideoTrack = stream.getVideoTracks()[0];
    } catch {
      return stream;
    }
    if (!firstVideoTrack) return stream;

    let settings: { width?: number; height?: number; frameRate?: number } = {};
    try {
      settings = firstVideoTrack.getSettings ? firstVideoTrack.getSettings() : {};
    } catch {
      try {
        settings = firstVideoTrack.getConstraints() as { width?: number; height?: number; frameRate?: number };
      } catch { /* empty */ }
    }

    const width = Math.round((settings.width ?? 1280) as number);
    const height = Math.round((settings.height ?? 720) as number);
    const frameRate = Math.round((settings.frameRate ?? 30) as number);

    this._segmentationMask = new ImageData(this._options.width, this._options.height);
    this._segmentationMaskCanvas.width = this._options.width;
    this._segmentationMaskCanvas.height = this._options.height;
    this._segmentationMaskCtx = this._segmentationMaskCanvas.getContext('2d');

    this._outputCanvasElement.width = width;
    this._outputCanvasElement.height = height;
    this._outputCanvasCtx = this._outputCanvasElement.getContext('2d');

    this._inputVideoElement.width = width;
    this._inputVideoElement.height = height;
    this._inputVideoElement.autoplay = true;
    this._inputVideoElement.muted = true;
    this._inputVideoElement.playsInline = true;
    this._inputVideoElement.srcObject = stream;

    const startLoop = () => {
      if (this._isRunning && !this._timerId) {
        this._scheduleNextFrame(frameRate);
      }
    };

    this._inputVideoElement.onloadeddata = startLoop;

    // Explicitly call play() — autoplay alone is not reliable for detached elements
    this._inputVideoElement.play().then(startLoop).catch(startLoop);

    return this._outputCanvasElement.captureStream(frameRate);
  }

  /**
   * Stops the capture and render loop.
   */
  stopEffect(): void {
    this._isRunning = false;
    if (this._timerId !== null) {
      clearTimeout(this._timerId);
      this._timerId = null;
    }
    if (this._inputVideoElement) {
      this._inputVideoElement.srcObject = null;
      this._inputVideoElement.onloadeddata = null;
    }
    this._stream = null;
  }

  private _scheduleNextFrame(frameRate: number): void {
    if (!this._isRunning) return;
    this._renderMask();
    this._timerId = setTimeout(() => {
      if (this._isRunning) this._scheduleNextFrame(frameRate);
    }, 1000 / frameRate);
  }

  private _renderMask(): void {
    this._resizeSource();
    this._runInference();
    this._runPostProcessing();
  }

  private _resizeSource(): void {
    if (!this._segmentationMaskCtx) return;

    this._segmentationMaskCtx.drawImage(
      this._inputVideoElement,
      0,
      0,
      this._inputVideoElement.width,
      this._inputVideoElement.height,
      0,
      0,
      this._options.width,
      this._options.height
    );

    const imageData = this._segmentationMaskCtx.getImageData(
      0,
      0,
      this._options.width,
      this._options.height
    );
    const inputMemoryOffset = this._model._getInputMemoryOffset() / 4;

    for (let i = 0; i < this._segmentationPixelCount; i++) {
      this._model.HEAPF32[inputMemoryOffset + (i * 3)] = imageData.data[i * 4] / 255;
      this._model.HEAPF32[inputMemoryOffset + (i * 3) + 1] = imageData.data[(i * 4) + 1] / 255;
      this._model.HEAPF32[inputMemoryOffset + (i * 3) + 2] = imageData.data[(i * 4) + 2] / 255;
    }
  }

  private _runInference(): void {
    if (!this._segmentationMask || !this._segmentationMaskCtx) return;

    this._model._runInference();
    const outputMemoryOffset = this._model._getOutputMemoryOffset() / 4;

    for (let i = 0; i < this._segmentationPixelCount; i++) {
      const person = this._model.HEAPF32[outputMemoryOffset + i];
      this._segmentationMask.data[(i * 4) + 3] = 255 * person;
    }

    this._segmentationMaskCtx.putImageData(this._segmentationMask, 0, 0);
  }

  private _runPostProcessing(): void {
    if (!this._outputCanvasCtx || !this._stream) return;

    const { backgroundType, blurValue } = this._options.virtualBackground;
    const { width, height } = this._outputCanvasElement;

    this._outputCanvasCtx.globalCompositeOperation = 'copy';
    // Smooth out the edges of the segmentation mask
    this._outputCanvasCtx.filter = backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE ? 'blur(4px)' : 'blur(8px)';
    this._outputCanvasCtx.drawImage(
      this._segmentationMaskCanvas,
      0,
      0,
      this._options.width,
      this._options.height,
      0,
      0,
      this._inputVideoElement.width,
      this._inputVideoElement.height
    );

    // Draw the foreground (person) on top
    this._outputCanvasCtx.globalCompositeOperation = 'source-in';
    this._outputCanvasCtx.filter = 'none';
    this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);

    // Draw the background behind the person
    this._outputCanvasCtx.globalCompositeOperation = 'destination-over';
    if (
      backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE &&
      this._virtualImage &&
      this._virtualImage.complete &&
      this._virtualImage.naturalWidth !== 0
    ) {
      this._outputCanvasCtx.drawImage(this._virtualImage, 0, 0, width, height);
    } else if (
      backgroundType === VIRTUAL_BACKGROUND_TYPE.BLUR ||
      (backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE &&
        (!this._virtualImage || !this._virtualImage.complete || this._virtualImage.naturalWidth === 0))
    ) {
      this._outputCanvasCtx.filter = `blur(${blurValue ?? 25}px)`;
      this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);
    }
  }
}
